import { extractErrorMessage } from '@shared/api/errorHandler';
import { User } from '@auth0/auth0-react';
import { getGreeting } from './greeting';
import { UnitsObject } from '@features/stock/entity';
import { SubscriptionPlans, SubscriptionPlansEnum } from '@features/subscription/entity';
import { VisitViewKey, VisitDetail } from '@features/visits/entity';
import AppTheme from '@libs/mui/AppTheme';
import { AppLanguage } from '@libs/redux/appUiSlice';
import { capitalize, type ButtonProps } from '@mui/material';
import type { CommonProps } from '@mui/material/OverridableComponent';
import {
  PHONE_LENGTH_BY_PREFIX,
  PHONE_PREFIXES,
} from '@shared/components/phone-prefix-autocomplete';
import { GridColDef } from '@mui/x-data-grid';
import type { AxiosError, AxiosResponse } from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { cloneElement, type ReactElement } from 'react';

export { getGreeting } from './greeting';

export const addPropsToReactElement = (
  element: ReactElement,
  props: ButtonProps & CommonProps & { color?: AppPaletteColor },
) => {
  return cloneElement(element, { ...props });
};

export const phoneValidationRule = {
  validate: (value: unknown, formValues: { phonePrefix?: string | null }) => {
    const str = typeof value === 'string' ? value : undefined;
    if (!str) return true;
    const prefix = formValues?.phonePrefix;
    const lengths = prefix ? PHONE_LENGTH_BY_PREFIX[prefix] : undefined;
    const min = lengths?.min ?? 9;
    const max = lengths?.max ?? 15;
    if (str.length < min || str.length > max) {
      return min === max ? `Číslo musí mít ${min} číslic.` : `Číslo musí mít ${min}–${max} číslic.`;
    }
    return true;
  },
};

export const lastNameValidationRule = {
  minLength: { value: 3, message: 'entity.lastNameValidation' },
};
export function formatToCZK(
  value: string | number | undefined,
  minimumFractionDigits?: number,
  maximumFractionDigits?: number,
): string {
  if (!value) {
    return '0,00 Kč';
  }
  const numberValue = typeof value === 'string' ? parseFloat(value.replace(',', '.')) : value;
  if (isNaN(numberValue)) {
    return '';
  }

  return numberValue.toLocaleString('cs-CZ', {
    style: 'currency',
    currency: 'CZK',
    minimumFractionDigits: minimumFractionDigits ?? 2,
    maximumFractionDigits: maximumFractionDigits ?? 2,
  });
}

export const getTimeFromUtcToLocal = (date: Date) => {
  return dayjs(date).format('HH:mm');
};
export const getDateTimeFromUtcToLocal = (date: Date) => {
  return dayjs(date).format('DD.MM.YYYY - HH:mm');
};
export const getDate = (date: Date) => {
  return dayjs(date).format('DD.MM.YYYY');
};
export const getDateShort = (date: Date) => {
  return dayjs(date).format('DD.MM.');
};
export const getDay = (date: Date) => capitalize(dayjs(date).format('dd'));

export const formatPhoneNumber = (digits: string | null): string | undefined => {
  if (!digits) {
    return undefined;
  }

  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
};

const KNOWN_PHONE_PREFIXES = PHONE_PREFIXES.map(({ dialCode }) => dialCode).sort(
  (first, second) => second.length - first.length,
);

/**
 * Parsuje telefonní číslo a oddělí mezinárodní předvolbu od lokálního čísla.
 * Funguje i pro čísla bez předvolby (výchozí předvolba je +420).
 */
export const parsePhoneWithPrefix = (raw: string): { phone: string; phonePrefix: string } => {
  let cleaned = raw.replace(/[\s\-().]/g, '');

  if (cleaned.startsWith('00') && cleaned.length > 10) {
    cleaned = '+' + cleaned.slice(2);
  }

  if (cleaned.startsWith('+')) {
    for (const prefix of KNOWN_PHONE_PREFIXES) {
      if (cleaned.startsWith(prefix)) {
        return { phonePrefix: prefix, phone: cleaned.slice(prefix.length) };
      }
    }
    const match = cleaned.match(/^\+(\d{1,3})(\d+)$/);
    if (match) {
      return { phonePrefix: '+' + match[1], phone: match[2] };
    }
  }

  const digits = cleaned.replace(/\D/g, '');
  return { phonePrefix: '+420', phone: digits.slice(-9) };
};

export const getButtonStyles = (active: boolean) => ({
  lineHeight: '18px',
  backgroundColor: active ? 'primary.main' : undefined,
  color: active ? 'white' : undefined,
});

export const getButtonStyle = <T>(tabelView: T, key: T) => {
  return tabelView === key ? 'contained' : 'text';
};

export type ApiErr = {
  message: string;
  code: number;
};

export const ApiError = (error: { message: string; statusCode: number }): ApiErr => ({
  message: error.message,
  code: error.statusCode,
});

type ApiCall<T> = () => Promise<AxiosResponse<T>>;

export const apiCall = async <T>(fn: ApiCall<T>, defaultErrorMessage: string): Promise<T> => {
  try {
    const response = await fn();
    return response.data;
  } catch (e: unknown) {
    const error = e as AxiosError<{ message?: string; error?: string }>;
    const errorObject = extractErrorMessage(error, defaultErrorMessage);
    throw ApiError(errorObject);
  }
};

export const getUserGreeting = (user?: User) => {
  if (!user?.family_name) {
    return '';
  }

  return getGreeting(user?.family_name);
};

export const isSubscriptionExpert = (plan: SubscriptionPlans | undefined): boolean => {
  return plan === SubscriptionPlansEnum.EXPERT;
};

export type Invoice = {
  invoiceNumber: string;
  customerName: string;
  customerEmail: string;
  notes: string;
  issuedAt: string;
  status: 'PAID' | 'CANCELLED' | 'PENDING';
  amount: number;
  currency: string;
};

export const APP_LAYOUT_WIDTH = 30;

export const getParsedFullName = (fullName: string) => {
  const splitedFullName = fullName.split(' ');

  if (splitedFullName.length === 1) {
    return { lastName: splitedFullName[0], firstName: '-' };
  } else {
    return { lastName: splitedFullName[1], firstName: splitedFullName[0] };
  }
};

export type DateRange = {
  from?: Date;
  to?: Date;
};

export type AppPalette = typeof AppTheme.palette;
export type AppPaletteColor = keyof AppPalette;
export type AppPaletteShade<C extends AppPaletteColor> = keyof AppPalette[C];

export type AppPaletteColorString = `${AppPaletteColor}.${string}`;

export const formatNameShort = (fullName: string | null | undefined): string => {
  if (!fullName) return '';
  const parts = fullName
    .trim()
    .split(/\s+/)
    .filter(p => p && p !== 'null' && p !== 'undefined');
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  const initial = parts[0].charAt(0).toUpperCase();
  const lastName = parts[parts.length - 1];
  return `${initial}. ${lastName}`;
};

export const formatTime = (seconds: number): string => {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts: string[] = [];
  if (days > 0) {
    parts.push(`${days}d`);
  }
  if (hours > 0 || days > 0) {
    parts.push(`${hours}h`);
  }
  if (mins > 0 || hours > 0 || days > 0) {
    parts.push(`${mins}m`);
  }
  parts.push(`${secs}s`);

  return parts.join(' ');
};

export type GetStockAllowance = {
  createdAt: Date;
  id: string;
  procedure: { visitId: string; visit: { clientId: string; date: Date } };
  procedureId: string;
  quantity: string;
  type: 'SALE' | 'CONSUMPTION';
  stockItem: { unit: string };
  stockItemName: string;
  avgUnitPrice: string;
  costs: string;
  stockItemId: string;
  teamId: string;
  user: { name: string };
  userId: string;
};

export type ConsumptionTableAllRecordType = {
  id: string;
  stockItemName: string;
  totalPrice: number;
  stockAllowanceQuantity: number;
  unit: string;
  type: 'SALE' | 'CONSUMPTION';
  user: string;
  date: Date;
  visitDate: Date;
  visitId: string;
  clientId: string;
};

export type ConsumptionTableByProductByUserType = {
  id: string;
  stockItemName: string;
  totalPrice: number;
  totalQuantity: number;
  unit: string;
  user: string;
  type: 'SALE' | 'CONSUMPTION';
};

export type UserStockItemAllowanceSummary = {
  id: string;
  user: string;
  type: 'SALE' | 'CONSUMPTION';
  stockItemName: string;
  unit: string;
  totalQuantity: number;
  totalPrice: number;
};

export type ConsumptionTableConfigType = {
  [StockView.byUser]: {
    rows: UserStockItemAllowanceSummary[];
    columns: GridColDef<ConsumptionTableByProductByUserType[][number]>[];
  };
  [StockView.byProduct]: {
    rows: UserStockItemAllowanceSummary[];
    columns: GridColDef<ConsumptionTableByProductByUserType[][number]>[];
  };
  [StockView.allRecords]: {
    rows: ConsumptionTableAllRecordType[];
    columns: GridColDef<ConsumptionTableAllRecordType[][number]>[];
  };
};

export enum StockView {
  byUser = 'byUser',
  byProduct = 'byProduct',
  allRecords = 'allRecords',
}

export type StockViewKey = keyof typeof StockView;

export const useScrollToTheTop = () => {
  function scrollToTop() {
    document.documentElement.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }

  return scrollToTop;
};

export const getSubscriptionText = (endDate: Date, status?: string): string => {
  switch (status) {
    case 'EXPIRED':
      return ' vypršelo';
    case 'CANCELLED':
      return ` bylo zrušeno, dne ${getDate(endDate)} se již nedostanete do aplikace.`;
    case 'PENDING':
      return ' není zaplacené, koukněte do emailu.';
    default:
      return ' je platné';
  }
};

export type MakePartial<T extends object, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export const unitList = Object.values(UnitsObject).map((unit, index) => ({
  id: String(index + 1),
  name: unit,
}));

export const SNACKBAR_TYPE = {
  ERROR: 'error',
  SUCCESS: 'success',
  WARNING: 'warning',
  INFO: 'info',
} as const;

export type SnackbarType = (typeof SNACKBAR_TYPE)[keyof typeof SNACKBAR_TYPE];

export type DayJsRange = {
  to: Dayjs;
  from: Dayjs;
};

export const typedEntries = <T extends object>(obj: T) => {
  return Object.entries(obj) as {
    [K in keyof T]: [K, T[K]];
  }[keyof T][];
};

export type DatesFilter = {
  [K in keyof DayJsRange]: DayJsRange[K] | string | undefined;
};

export type PersistentFiltersType = {
  visits: {
    dashBoardVisitOverView: { dates: DatesFilter; view: 'byAll' };
    allVisitsPage: {
      dates: DatesFilter;
      view: VisitViewKey;
    };
  };
  revenue: { dates: DatesFilter };
  consumption: {
    dates: DatesFilter;
    view: StockViewKey;
  };
  language: AppLanguage;
};

export type VisitListApplyFilter = keyof PersistentFiltersType['visits'] | 'onlyOpenVisits';

type VisitMap = Map<string, { cost: number; revenue: number }>;
type Result = { costs: number[]; revenue: number[]; profit: number[]; labels: string[] };

const DAY_LABEL_FORMAT = 'YYYY-MM-DD';
const MONTH_LABEL_FORMAT = 'YYYY-MM';

const isDayLabel = (label: string): boolean => dayjs(label, DAY_LABEL_FORMAT, true).isValid();
const isMonthLabel = (label: string): boolean => dayjs(label, MONTH_LABEL_FORMAT, true).isValid();

const labelToDayKey = (iso: string) => {
  return dayjs(iso).format('D.M.');
};

const labelToMonthKey = (label: string): string => {
  return dayjs(label).format('MMMM YYYY');
};

const aggregateMonth = (visitMap: VisitMap, key: string): { cost: number; revenue: number } => {
  let monthCost = 0;
  let monthRevenue = 0;

  for (const [dateKey, val] of visitMap.entries()) {
    if (dateKey.startsWith(key)) {
      monthCost += val.cost;
      monthRevenue += val.revenue;
    }
  }

  return { cost: monthCost, revenue: monthRevenue };
};

const isColumnPerDay = (label: string) => label.length === 10;
const isColumnPerMonth = (label: string) => label.length === 7;

const getAggregatedFinancialStatsPerColumn = (
  label: string,
  visitMap: VisitMap,
): { costsPerColumn: number; revenuePerColumn: number; profitPerColumn: number } => {
  if (isColumnPerDay(label)) {
    const { cost, revenue } = visitMap.get(label) ?? {
      cost: 0,
      revenue: 0,
    };

    return {
      costsPerColumn: Math.round(cost),
      revenuePerColumn: Math.round(revenue),
      profitPerColumn: Math.round(revenue - cost),
    };
  } else if (isColumnPerMonth(label)) {
    const { cost, revenue } = aggregateMonth(visitMap, label);

    return {
      costsPerColumn: cost,
      revenuePerColumn: revenue,
      profitPerColumn: revenue - cost,
    };
  }

  return { costsPerColumn: 0, revenuePerColumn: 0, profitPerColumn: 0 };
};

const getVisitKey = (visitDate: Date): string => {
  return dayjs(visitDate).format('YYYY-MM-DD');
};

export const DEFAULT_PERSISTENT_FILTERS: PersistentFiltersType = {
  visits: {
    dashBoardVisitOverView: {
      dates: {
        from: dayjs().subtract(1, 'day').toISOString(),
        to: dayjs().add(1, 'day').toISOString(),
      },
      view: 'byAll',
    },
    allVisitsPage: {
      dates: {
        from: dayjs().subtract(1, 'day').toISOString(),
        to: dayjs().add(1, 'day').toISOString(),
      },
      view: 'byAll',
    },
  },
  revenue: { dates: { from: dayjs().subtract(7, 'day').toISOString(), to: dayjs().toISOString() } },
  consumption: {
    dates: {
      from: dayjs().startOf('month').toISOString(),
      to: dayjs().endOf('month').toISOString(),
    },
    view: 'byUser',
  },
  language: 'cs',
};

export const DEMO_DEFAULT_PERSISTENT_FILTERS: PersistentFiltersType = {
  visits: {
    dashBoardVisitOverView: {
      dates: {
        from: dayjs('2026-01-20').startOf('day').toISOString(),
        to: dayjs('2026-02-02').endOf('day').toISOString(),
      },
      view: 'byAll',
    },
    allVisitsPage: {
      dates: {
        from: dayjs('2025-12-01').toISOString(),
        to: dayjs('2026-01-21').toISOString(),
      },
      view: 'byAll',
    },
  },
  revenue: {
    dates: { from: dayjs('2025-12-01').toISOString(), to: dayjs('2026-03-01').toISOString() },
  },
  consumption: {
    dates: {
      from: dayjs('2026-01-01').toISOString(),
      to: dayjs('2026-01-21').toISOString(),
    },
    view: 'allRecords',
  },
  language: 'cs',
};

export const STORAGE_KEY = 'persistentFilters';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getValidStoredDateRange = (value: unknown, fallback: DatesFilter): DatesFilter => {
  if (!isRecord(value)) {
    return fallback;
  }

  const from = value.from;
  const to = value.to;

  if (
    (typeof from !== 'string' && !dayjs.isDayjs(from)) ||
    (typeof to !== 'string' && !dayjs.isDayjs(to)) ||
    !dayjs(from).isValid() ||
    !dayjs(to).isValid() ||
    dayjs(from).isAfter(dayjs(to))
  ) {
    return fallback;
  }

  return { from, to };
};

export const getPersistentFiltersFromLocalStorage = (isDemo: boolean): PersistentFiltersType => {
  const DEFAULT_PERSISTENT_FILTERS_TO_USE = isDemo
    ? DEMO_DEFAULT_PERSISTENT_FILTERS
    : DEFAULT_PERSISTENT_FILTERS;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return DEFAULT_PERSISTENT_FILTERS_TO_USE;
    }

    const parsed = JSON.parse(stored) as unknown;

    if (!isRecord(parsed)) {
      return DEFAULT_PERSISTENT_FILTERS_TO_USE;
    }

    const storedFilters = parsed as Partial<PersistentFiltersType>;
    const persistentFilters: PersistentFiltersType = {
      ...DEFAULT_PERSISTENT_FILTERS_TO_USE,
      ...storedFilters,
      visits: {
        dashBoardVisitOverView: {
          ...DEFAULT_PERSISTENT_FILTERS_TO_USE.visits.dashBoardVisitOverView,
          ...storedFilters.visits?.dashBoardVisitOverView,
          dates: getValidStoredDateRange(
            storedFilters.visits?.dashBoardVisitOverView?.dates,
            DEFAULT_PERSISTENT_FILTERS_TO_USE.visits.dashBoardVisitOverView.dates,
          ),
        },
        allVisitsPage: {
          ...DEFAULT_PERSISTENT_FILTERS_TO_USE.visits.allVisitsPage,
          ...storedFilters.visits?.allVisitsPage,
          dates: getValidStoredDateRange(
            storedFilters.visits?.allVisitsPage?.dates,
            DEFAULT_PERSISTENT_FILTERS_TO_USE.visits.allVisitsPage.dates,
          ),
        },
      },
      revenue: {
        ...DEFAULT_PERSISTENT_FILTERS_TO_USE.revenue,
        ...storedFilters.revenue,
        dates: getValidStoredDateRange(
          storedFilters.revenue?.dates,
          DEFAULT_PERSISTENT_FILTERS_TO_USE.revenue.dates,
        ),
      },
      consumption: {
        ...DEFAULT_PERSISTENT_FILTERS_TO_USE.consumption,
        ...storedFilters.consumption,
        dates: getValidStoredDateRange(
          storedFilters.consumption?.dates,
          DEFAULT_PERSISTENT_FILTERS_TO_USE.consumption.dates,
        ),
      },
    };

    if (isDemo) {
      persistentFilters.visits.dashBoardVisitOverView =
        DEMO_DEFAULT_PERSISTENT_FILTERS.visits.dashBoardVisitOverView;
    }

    return persistentFilters;
  } catch {
    return DEFAULT_PERSISTENT_FILTERS_TO_USE;
  }
};

const aggregateVisitsByDay = (visits: VisitDetail[], range?: DateRange): VisitMap => {
  const map: VisitMap = new Map();

  for (const visitDetail of visits) {
    const { visit, financials, loyalty } = visitDetail;
    const visitDate = dayjs(visit.dateFrom);
    const visitOpen = !visit.visitStatus;

    if (range?.from && visitDate.isBefore(dayjs(range.from), 'day')) {
      continue;
    }

    if (range?.to && visitDate.isAfter(dayjs(range.to), 'day')) {
      continue;
    }

    if (visitOpen) {
      continue;
    }
    const key = getVisitKey(visitDate.toDate());

    const priceAfterLoyaltyPointsSubtraction =
      (Number(financials.paidPrice) || 0) - (Number(loyalty.loyaltyPointsUsed) || 0);
    const revenueForServices = parseFloat(`${priceAfterLoyaltyPointsSubtraction}`);
    const revenueForGoods = parseFloat(`${financials.totalSalesRevenue}`);
    const revenue = revenueForServices + revenueForGoods;
    const cost = calculateVisitCost(visitDetail);
    const existing = map.get(key);

    if (existing) {
      existing.revenue += revenue;
      existing.cost += cost;
    } else {
      map.set(key, { cost, revenue });
    }
  }

  return map;
};

const calculateVisitCost = (visitDetail: VisitDetail): number => {
  let total = 0;
  const { procedures } = visitDetail;

  for (const procedure of procedures ?? []) {
    for (const allowance of procedure.stockAllowances ?? []) {
      // const qty = parseFloat(`${allowance.quantity}`);
      // const avgUnitPriceAtTheTime = parseFloat(`${allowance.avgUnitPrice}`);
      total += Number(allowance.costs);
    }
  }

  return total;
};

const getMonthLabelsInRange = (from: dayjs.Dayjs, to: dayjs.Dayjs): string[] => {
  const labels: string[] = [];
  let cursor = from.startOf('month');

  while (cursor.isBefore(to) || cursor.isSame(to, 'month')) {
    labels.push(cursor.format(MONTH_LABEL_FORMAT));
    cursor = cursor.add(1, 'month');
  }

  return labels;
};

const getDayLabelsInRange = (from: dayjs.Dayjs, to: dayjs.Dayjs): string[] => {
  const labels: string[] = [];
  let cursor = from;
  while (cursor.isBefore(to) || cursor.isSame(to, 'day')) {
    labels.push(cursor.format(DAY_LABEL_FORMAT));
    cursor = cursor.add(1, 'day');
  }
  return labels;
};

const getDateLabelsInFilteredDateRange = (map: VisitMap, range?: DateRange): string[] => {
  if (range?.from && range?.to) {
    const from = dayjs(range.from).startOf('day');
    const to = dayjs(range.to).startOf('day');
    const diffDays = to.diff(from, 'day');

    if (diffDays > 60) {
      return getMonthLabelsInRange(from, to);
    } else {
      return getDayLabelsInRange(from, to);
    }
  } else {
    const labels = Array.from(map.keys());
    labels.sort((a, b) => dayjs(a).unix() - dayjs(b).unix());

    return labels;
  }
};

const getMonthOrDayColumnLabel = (date: string) => {
  if (isDayLabel(date)) {
    return labelToDayKey(date);
  } else if (isMonthLabel(date)) {
    return labelToMonthKey(date);
  } else {
    return date;
  }
};

export const getFinancialDataByDateRange = (visits: VisitDetail[], range?: DateRange): Result => {
  const costsAndRevenueByDay = aggregateVisitsByDay(visits, range);

  const columnLabelsByFilteredDateRange = getDateLabelsInFilteredDateRange(
    costsAndRevenueByDay,
    range,
  );

  const { costsArrayByColumn, revenueArrayByColumn, profitArrayByColumn } =
    getCostsRevenueProfitArrayByColumn(columnLabelsByFilteredDateRange, costsAndRevenueByDay);

  const formatLabels = getColumnLabels(columnLabelsByFilteredDateRange);

  return {
    labels: formatLabels,
    costs: costsArrayByColumn,
    revenue: revenueArrayByColumn,
    profit: profitArrayByColumn,
  };
};

const getColumnLabels = (columnLabelsByFilteredDateRange: string[]) => {
  const labels: string[] = [];

  for (const date of columnLabelsByFilteredDateRange) {
    const monthOrDaycolumnLabel = getMonthOrDayColumnLabel(date);
    labels.push(monthOrDaycolumnLabel);
  }

  return labels;
};

const getCostsRevenueProfitArrayByColumn = (
  columnLabelsByFilteredDateRange: string[],
  costsAndRevenueByDay: VisitMap,
) => {
  const costsArrayByColumn: number[] = [];
  const revenueArrayByColumn: number[] = [];
  const profitArrayByColumn: number[] = [];

  for (const date of columnLabelsByFilteredDateRange) {
    const {
      costsPerColumn: costsPerVisit,
      revenuePerColumn: revenuePerVisit,
      profitPerColumn: profitPerVisit,
    } = getAggregatedFinancialStatsPerColumn(date, costsAndRevenueByDay);

    costsArrayByColumn.push(costsPerVisit);
    revenueArrayByColumn.push(revenuePerVisit);
    profitArrayByColumn.push(profitPerVisit);
  }

  return { costsArrayByColumn, revenueArrayByColumn, profitArrayByColumn };
};

export const formatPercentage = (value: number) => {
  return new Intl.NumberFormat('cs-CZ', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value / 100);
};

export const formatNumber = (value: number) => {
  return new Intl.NumberFormat('cs-CZ', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(value);
};
